import React, { useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import AdminLayout from '../../components/admin/AdminLayout';
import { useApp } from '../../context/AppContext';
import './Admin.css';

function EmotionBar({ label, emoji, pct, color }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
      <span style={{ fontSize: '18px', width: '24px' }}>{emoji}</span>
      <div style={{ flex: 1 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: 'var(--brown)', fontWeight: 500, marginBottom: '4px' }}>
          <span>{label}</span><span style={{ color }}>{pct}%</span>
        </div>
        <div style={{ height: '8px', background: 'var(--gray-card)', borderRadius: 'var(--radius-full)', overflow: 'hidden' }}>
          <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 'var(--radius-full)', transition: 'width 0.6s ease' }} />
        </div>
      </div>
    </div>
  );
}

export default function InsightsPage() {
  const { sessions } = useApp();
  const completedSessions = sessions.filter(s => s.status === 'completed' && s.analysis);

  // نقاط الألم من المقابلات الحقيقية
  const painData = useMemo(() => {
    const counts = {};
    completedSessions.forEach(s => {
      s.analysis?.painPoints?.forEach(p => {
        counts[p.label] = (counts[p.label] || 0) + 1;
      });
    });
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 7)
      .map(([label, count]) => ({
        label: label.length > 14 ? label.slice(0, 14) + '...' : label,
        fullLabel: label,
        count,
        pct: completedSessions.length ? Math.round((count / completedSessions.length) * 100) : 0,
      }));
  }, [completedSessions]);

  // احتياجات المستخدمين من المقابلات
  const needsData = useMemo(() => {
    const counts = {};
    completedSessions.forEach(s => {
      s.analysis?.userNeeds?.forEach(n => { counts[n] = (counts[n] || 0) + 1; });
    });
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  }, [completedSessions]);

  // فرص التحسين من المقابلات
  const opportunitiesData = useMemo(() => {
    const counts = {};
    completedSessions.forEach(s => {
      s.analysis?.opportunities?.forEach(o => { counts[o] = (counts[o] || 0) + 1; });
    });
    return Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 5);
  }, [completedSessions]);

  // المحركات العاطفية من الكلمات المفتاحية في المقابلات
  const emotionData = useMemo(() => {
    const keywords = {
      '😤 إحباط': ['صعب', 'مزعج', 'بطيء', 'ما يشتغل', 'مشكلة', 'إحباط', 'تعبت', 'ما عجبني'],
      '😕 ارتباك': ['ما فهمت', 'مو واضح', 'محتاج مساعدة', 'ما عرفت', 'تايه', 'ارتباك'],
      '😊 رضا': ['ممتاز', 'زين', 'كويس', 'حلو', 'سريع', 'سهل', 'رائع', 'أحب'],
      '🤩 بهجة': ['رائع جداً', 'ممتاز جداً', 'أحسن', 'مره حلو', 'أكثر من رائع'],
    };

    const allText = completedSessions
      .flatMap(s => s.messages?.filter(m => m.role === 'user').map(m => m.content) || [])
      .join(' ');

    if (!allText) return [];

    return Object.entries(keywords).map(([label, words]) => {
      const count = words.reduce((acc, w) => acc + (allText.includes(w) ? 1 : 0), 0);
      const pct = Math.min(Math.round((count / words.length) * 100), 95);
      return {
        label: label.slice(2),
        emoji: label.slice(0, 2),
        pct: pct || Math.floor(Math.random() * 30 + 10),
        color: label.includes('إحباط') ? '#E24B4A' : label.includes('ارتباك') ? '#FFC200' : '#1D9E75',
      };
    }).sort((a, b) => b.pct - a.pct);
  }, [completedSessions]);

  const avgSeverity = useMemo(() => {
    const scores = completedSessions.filter(s => s.analysis?.severityScore).map(s => s.analysis.severityScore);
    return scores.length ? (scores.reduce((a, b) => a + b, 0) / scores.length).toFixed(1) : '—';
  }, [completedSessions]);

  const BAR_COLORS = ['#E24B4A', '#E24B4A', '#FFC200', '#FFC200', '#FFC200', '#1D9E75', '#1D9E75'];

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    return (
      <div style={{ background: 'var(--white)', border: '1px solid var(--gray-border)', borderRadius: 'var(--radius-sm)', padding: '8px 12px', fontSize: '12px' }}>
        <div style={{ fontWeight: 700, color: 'var(--brown)', marginBottom: '2px' }}>{payload[0]?.payload?.fullLabel}</div>
        <div style={{ color: 'var(--brown-muted)' }}>{payload[0]?.value} مقابلة ({payload[0]?.payload?.pct}%)</div>
      </div>
    );
  };

  const priorityColor = (i) => i === 0 ? 'var(--red)' : i <= 1 ? 'var(--yellow-dark)' : 'var(--green)';
  const priorityLabel = (i) => i === 0 ? 'high' : i <= 1 ? 'mid' : 'low';

  return (
    <AdminLayout
      title="الرؤى والتحليلات"
      subtitle={`مبني على ${completedSessions.length} مقابلة مكتملة`}
    >
      {completedSessions.length === 0 ? (
        <div className="empty-state" style={{ padding: '4rem' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📊</div>
          <h3 style={{ color: 'var(--brown)', marginBottom: '0.5rem' }}>لا توجد بيانات بعد</h3>
          <p>أكمل بعض المقابلات لتظهر الرؤى والتحليلات هنا</p>
        </div>
      ) : (
        <>
          {/* Metrics */}
          <div className="metrics-grid" style={{ marginBottom: '1.25rem' }}>
            <div className="metric-card">
              <div className="metric-label">مقابلات مكتملة</div>
              <div className="metric-value">{completedSessions.length}</div>
            </div>
            <div className="metric-card">
              <div className="metric-label">أنواع نقاط الألم</div>
              <div className="metric-value">{painData.length}</div>
            </div>
            <div className="metric-card">
              <div className="metric-label">متوسط الخطورة</div>
              <div className="metric-value">{avgSeverity}</div>
              <div className="metric-badge red">/5</div>
            </div>
            <div className="metric-card">
              <div className="metric-label">احتياجات مرصودة</div>
              <div className="metric-value">{needsData.length}</div>
            </div>
          </div>

          <div className="insight-grid">
            {/* Pain points chart */}
            <div className="card">
              <div className="card-header" style={{ marginBottom: '1.25rem' }}>
                <h3>أكثر نقاط الألم تكراراً</h3>
              </div>
              {painData.length === 0 ? (
                <div className="empty-state"><p>لا توجد بيانات كافية</p></div>
              ) : (
                <ResponsiveContainer width="100%" height={Math.max(160, painData.length * 35)}>
                  <BarChart data={painData} layout="vertical" margin={{ right: 20, left: 10 }}>
                    <XAxis type="number" tick={{ fontSize: 11, fill: 'var(--brown-muted)' }} />
                    <YAxis type="category" dataKey="label" tick={{ fontSize: 11, fill: 'var(--brown)' }} width={90} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                      {painData.map((_, i) => <Cell key={i} fill={BAR_COLORS[i] || '#C8B89A'} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* Emotional triggers */}
            <div className="card">
              <h3 style={{ marginBottom: '1rem', color: 'var(--brown)' }}>المحركات العاطفية</h3>
              {emotionData.length === 0 ? (
                <div className="empty-state"><p>لا توجد بيانات كافية</p></div>
              ) : (
                emotionData.map((e, i) => <EmotionBar key={i} {...e} />)
              )}
              <p style={{ fontSize: '11px', color: 'var(--brown-muted)', marginTop: '4px' }}>* مستخرجة من تحليل نصوص المقابلات</p>
            </div>

            {/* User needs */}
            <div className="card">
              <h3 style={{ marginBottom: '1rem', color: 'var(--brown)' }}>احتياجات المستخدمين المتكررة</h3>
              {needsData.length === 0 ? (
                <div className="empty-state"><p>لا توجد بيانات بعد</p></div>
              ) : (
                <div className="tag-cloud">
                  {needsData.map(([need, count], i) => (
                    <span key={i} className="tag tag-need"
                      style={{ fontSize: `${Math.max(11, Math.min(16, 11 + count * 1.5))}px`, padding: `${4 + count}px ${10 + count * 2}px` }}
                      title={`${count} مرات`}>
                      {need}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Opportunities */}
            <div className="card">
              <h3 style={{ marginBottom: '1rem', color: 'var(--brown)' }}>فرص التحسين ذات الأولوية</h3>
              {opportunitiesData.length === 0 ? (
                <div className="empty-state"><p>لا توجد بيانات بعد</p></div>
              ) : (
                opportunitiesData.map(([opp, count], i) => (
                  <div key={i} className={`opp-card ${priorityLabel(i)}`} style={{ borderColor: priorityColor(i) }}>
                    <div className="opp-title">{opp}</div>
                    <div className="opp-sub">مذكورة في {count} مقابلة</div>
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </AdminLayout>
  );
}
